import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { clearAgencyCache } from "@/hooks/useAgencyData";
import { agencyCacheKeys, readAgencyCacheEntry, removeAgencyCacheKey, writeAgencyCache } from "@/lib/agency-cache";
import { loadValidatedSession } from "@/lib/auth-session";
import { hideSplash } from "@/lib/splash";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (data: SignUpData) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
  checkSubscription: () => Promise<void>;
}

interface Profile {
  id: string;
  created_at: string;
  name: string;
  email: string;
  slug: string;
  bio: string | null;
  photo_url: string | null;
  is_pro: boolean;
  period_end: string;
  referral_code: string;
  onboarding_done: boolean;
  billing_interval: "monthly" | "annual" | null;
  stripe_customer_id: string | null;
  pro_credit_months: number;
  subscription_cancelled: boolean;
  referral_credits_frozen: boolean;
  niche: string | null;
  instagram_url: string | null;
  tiktok_url: string | null;
  youtube_url: string | null;
  linkedin_url: string | null;
  snapchat_url: string | null;
  twitch_url: string | null;
  twitter_url: string | null;
  pinterest_url: string | null;
  agency_id: string | null;
  credit_expires_at: string | null;
}

interface SignUpData {
  name: string;
  email: string;
  password: string;
  bio?: string;
  photo?: File | null;
  referralCode?: string;
  username?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

// Generate a unique slug with collision handling
async function generateUniqueSlug(name: string): Promise<string> {
  const base = name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();

  if (!base) return `user-${Date.now()}`;

  const { data } = await supabase
    .from("profiles")
    .select("slug")
    .like("slug", `${base}%`);

  if (!data || data.length === 0) return base;

  const existingSlugs = new Set(data.map((p) => p.slug));
  if (!existingSlugs.has(base)) return base;

  let counter = 2;
  while (existingSlugs.has(`${base}-${counter}`)) counter++;
  return `${base}-${counter}`;
}

// Minimum ms between subscription checks
const SUB_CHECK_COOLDOWN = 60_000;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // Dedup guards
  const fetchingProfile = useRef<string | null>(null);
  const lastSubCheck = useRef(0);
  const bootSplashHiddenRef = useRef(false);
  // Track current profile for agency detection even when JWT metadata is stale
  const profileRef = useRef<Profile | null>(null);

  // Keep profileRef in sync
  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  const fetchProfile = useCallback(async (userId: string) => {
    // Prevent duplicate concurrent fetches for the same user
    if (fetchingProfile.current === userId) return;
    fetchingProfile.current = userId;
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();
      if (data) {
        const nextProfile = data as Profile;
        setProfile(nextProfile);
        writeAgencyCache(agencyCacheKeys.profile(userId), nextProfile);
      } else if (error && error.code === "PGRST116") {
        // PGRST116 = "no rows from .single()". On an RLS-protected table this
        // means EITHER the row doesn't exist OR RLS blocked the query because
        // the JWT couldn't be validated (e.g. during a server restart).
        //
        // If we already have a cached profile for this user, the row exists —
        // the failure is transient. Keep the cached profile and retry later.
        const hadCachedProfile = !!profileRef.current && profileRef.current.id === userId;

        if (hadCachedProfile) {
          // Transient failure — profile exists but RLS blocked the query.
          // Schedule a background retry; don't touch current state.
          console.warn("[AUTH] Profile fetch blocked (likely transient RLS) — keeping cached profile, retrying in 10s");
          setTimeout(() => {
            fetchingProfile.current = null; // allow the retry
            void fetchProfile(userId);
          }, 10_000);
          return;
        }

        // No cached profile — this is likely a brand-new account where the
        // trigger hasn't created the row yet. Retry with back-off.
        // Before retrying, verify the session is still valid.
        const { data: { session: preRetrySession } } = await supabase.auth.getSession();
        if (!preRetrySession) {
          console.warn("Session lost before profile retries — skipping");
          return;
        }

        const delays = [1500, 3000, 5000, 7000, 10000];
        let resolved = false;
        for (const delay of delays) {
          await new Promise((r) => setTimeout(r, delay));
          const { data: retryData, error: retryError } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", userId)
            .single();
          if (retryData) {
            const nextProfile = retryData as Profile;
            setProfile(nextProfile);
            writeAgencyCache(agencyCacheKeys.profile(userId), nextProfile);
            resolved = true;
            break;
          }
          if (retryError && retryError.code !== "PGRST116") break;
        }
        if (!resolved) {
          // Even after retries the profile wasn't found. If the session is still
          // valid, this is a prolonged infrastructure issue — do NOT sign out.
          // The user's session is legitimate; the profile will appear once the
          // DB/PostgREST stabilizes.
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          if (currentSession) {
            console.warn("[AUTH] Profile not found after retries but session still valid — scheduling deferred retry");
            setTimeout(() => {
              fetchingProfile.current = null;
              void fetchProfile(userId);
            }, 15_000);
          } else {
            console.warn("[AUTH] Session lost during profile retries — not forcing sign-out");
          }
        }
      }
    } finally {
      fetchingProfile.current = null;
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user) await fetchProfile(user.id);
  }, [user, fetchProfile]);

  const hydrateProfileFromCache = useCallback((userId: string) => {
    const cached = readAgencyCacheEntry<Profile>(agencyCacheKeys.profile(userId));
    if (!cached) return false;
    setProfile(cached.value);
    return true;
  }, []);

  const checkSubscription = useCallback(async () => {
    // Use getSession (local/cached) instead of loadValidatedSession to avoid
    // re-calling getUser() which can fail transiently after Stripe redirects
    // and cause an unnecessary sign-out. The session is already validated on boot.
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    if (!currentSession?.user) return;

    const now = Date.now();
    if (now - lastSubCheck.current < SUB_CHECK_COOLDOWN) return;
    lastSubCheck.current = now;

    try {
      const { error } = await supabase.functions.invoke("check-subscription");
      if (error) {
        console.error("check-subscription error:", error);
        return;
      }
      await fetchProfile(currentSession.user.id);
    } catch (e) {
      console.error("check-subscription failed:", e);
    }
  }, [fetchProfile]);

  useEffect(() => {
    let mountedUserId: string | null = null;

    const syncSessionState = (nextSession: Session | null, source?: string) => {
      console.info(`[AUTH-DEBUG] syncSessionState called | source=${source ?? "unknown"} | hasSession=${!!nextSession} | userId=${nextSession?.user?.id?.slice(0, 8) ?? "none"} | prevUserId=${mountedUserId?.slice(0, 8) ?? "none"}`);
      setSession(nextSession);
      const nextUser = nextSession?.user ?? null;
      setUser(nextUser);

      if (!nextUser) {
        console.info(`[AUTH-DEBUG] No user in session — clearing state | source=${source}`);
        mountedUserId = null;
        setProfile(null);
        setLoading(false);
        return;
      }

      const hasCachedProfile = hydrateProfileFromCache(nextUser.id);
      if (hasCachedProfile) setLoading(false);

      if (mountedUserId !== nextUser.id) {
        mountedUserId = nextUser.id;
        if (!hasCachedProfile) setLoading(true);
        void fetchProfile(nextUser.id).finally(() => setLoading(false));
        return;
      }

      setLoading(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        console.info(`[AUTH-DEBUG] onAuthStateChange | event=${_event} | hasSession=${!!session} | userId=${session?.user?.id?.slice(0, 8) ?? "none"}`);
        syncSessionState(session, `authChange:${_event}`);
      }
    );

    loadValidatedSession().then(({ session, invalid }) => {
      console.info(`[AUTH-DEBUG] loadValidatedSession complete | hasSession=${!!session} | invalid=${invalid}`);
      if (invalid) {
        syncSessionState(null, "boot:invalid");
        return;
      }
      syncSessionState(session, "boot:valid");
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile, hydrateProfileFromCache]);

  useEffect(() => {
    if (loading || bootSplashHiddenRef.current) return;

    bootSplashHiddenRef.current = true;
    requestAnimationFrame(() => {
      void hideSplash();
    });
  }, [loading]);

  // Check subscription on login and periodically (deferred, non-blocking)
  useEffect(() => {
    if (!user) return;
    // Defer the initial check so profile loads first
    const timeout = setTimeout(checkSubscription, 500);
    // Re-check every 5 minutes
    const interval = setInterval(checkSubscription, 300_000);
    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [user, checkSubscription]);

  const signUp = async ({ name, email, password, bio, photo, referralCode, username }: SignUpData) => {
    const slug = username || await generateUniqueSlug(name);

    const metadata: Record<string, string> = { name, slug };
    if (bio) metadata.bio = bio;
    if (referralCode) metadata.referral_code = referralCode;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `https://sendcollab.to/verify-email?email=${encodeURIComponent(email)}`,
        data: metadata,
      },
    });
    if (error) throw error;

    if (data.user?.identities?.length === 0) {
      throw new Error("An account with this email already exists. Please log in instead.");
    }

    const userId = data.user?.id;
    if (!userId) return;

    if (photo) {
      const ext = photo.name.split(".").pop();
      const path = `${userId}/avatar.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(path, photo, { upsert: true });
      if (!uploadError) {
        const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(path);
        await supabase.from("profiles").update({ photo_url: urlData.publicUrl }).eq("id", userId).then(() => {});
      }
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    console.info(`[AUTH-DEBUG] signOut() called | stack:`, new Error().stack?.split("\n").slice(1, 4).join(" <- "));
    lastSubCheck.current = 0;
    clearAgencyCache();
    try {
      await supabase.auth.signOut();
    } catch {
      localStorage.removeItem(`sb-${import.meta.env.VITE_SUPABASE_PROJECT_ID}-auth-token`);
    }
    setSession(null);
    setUser(null);
    setProfile(null);
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: "https://sendcollab.to/reset-password",
    });
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{ session, user, profile, loading, signUp, signIn, signOut, resetPassword, refreshProfile, checkSubscription }}>
      {children}
    </AuthContext.Provider>
  );
};
